<template>
  <div class="textarea-wrapper">
    <label v-if="label" :for="textareaId" class="block text-sm font-medium text-text text-left mb-1">
      {{ label }}
      <span v-if="required" class="text-error ml-1">*</span>
    </label>

    <div class="relative">
      <textarea
        ref="textareaRef"
        :id="textareaId"
        :name="name"
        :value="modelValue"
        :placeholder="resolvedPlaceholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :aria-label="props.ariaLabel"
        :aria-describedby="describedBy"
        :autocomplete="autocomplete"
        :minlength="minlength"
        :maxlength="maxlength"
        :rows="rows"
        :cols="cols"
        :wrap="wrap"
        :class="textareaClass"
        @input="$emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @keydown="$emit('keydown', $event)"
        @keyup="$emit('keyup', $event)"
      />
    </div>

    <div v-if="helpText && !errorText" :id="`${textareaId}-help`" class="mt-1 text-sm text-text-muted">
      {{ helpText }}
    </div>

    <div v-if="errorText" :id="`${textareaId}-error`" class="mt-1 text-sm text-error">
      {{ errorText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useTextareaAutoResize } from '@/composables/useTextareaAutoResize'
import { WEEKLY_CHECK_IN_SUPPRESS_PLACEHOLDERS } from '@/constants/weeklyCheckInPrintContext'

interface Props {
  modelValue?: string
  state?: 'default' | 'success' | 'warning' | 'danger'
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  placeholder?: string
  label?: string
  helpText?: string
  errorText?: string
  ariaLabel?: string
  ariaDescribedby?: string
  autocomplete?: string
  minlength?: number
  maxlength?: number
  rows?: number
  cols?: number
  wrap?: 'soft' | 'hard' | 'off'
  name?: string
  /** When true (default), height grows with content; no inner scrollbar. */
  autoResize?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  state: 'default',
  disabled: false,
  readonly: false,
  required: false,
  rows: 3,
  cols: 50,
  wrap: 'soft',
  autoResize: true
})

const suppressPrintPlaceholders = inject(WEEKLY_CHECK_IN_SUPPRESS_PLACEHOLDERS, false)

const resolvedPlaceholder = computed(() =>
  suppressPrintPlaceholders ? undefined : props.placeholder
)

defineEmits<{
  'update:modelValue': [value: string]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
  keydown: [event: KeyboardEvent]
  keyup: [event: KeyboardEvent]
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)

useTextareaAutoResize(textareaRef, {
  enabled: () => props.autoResize,
  modelValue: () => props.modelValue,
  rows: () => props.rows
})

const textareaClass = computed(() => [
  'textarea-field',
  `state-${props.state}`,
  props.autoResize ? 'textarea-field--auto-resize' : 'textarea-field--fixed-rows'
])

// Generate unique ID for accessibility
const textareaId = computed(() => `textarea-${Math.random().toString(36).substring(2, 11)}`)

// Computed aria-describedby for accessibility
const describedBy = computed(() => {
  const ids = []
  if (props.helpText && !props.errorText) ids.push(`${textareaId.value}-help`)
  if (props.errorText) ids.push(`${textareaId.value}-error`)
  return ids.length > 0 ? ids.join(' ') : undefined
})
</script>

<style scoped>
.textarea-field {
  /* Block-level replaced control: avoids ~4px baseline gap under inline/inline-block textareas inside the `.relative` wrapper */
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  border: 1px solid;
  border-radius: 0.375rem;

  min-height: 2.5rem;

  /* Use theme variables */
  background-color: var(--color-input-background);
  border-color: var(--color-input-border);
  color: var(--color-input-text);
}

.textarea-field--auto-resize {
  resize: none;
  overflow-y: hidden;
}

.textarea-field--fixed-rows {
  resize: vertical;
}

.textarea-field:focus {
  outline: none;
  border-color: var(--color-input-focus);
  box-shadow: 0 0 0 2px var(--color-input-focus);
}

.textarea-field:focus-visible {
  outline: 2px solid var(--color-input-focus);
  outline-offset: 2px;
}

.textarea-field:disabled {
  background-color: var(--color-surface-muted);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.textarea-field:read-only {
  background-color: var(--color-surface-muted);
  cursor: default;
}

.textarea-field::placeholder {
  color: var(--color-input-placeholder);
}

/* State variants */
.textarea-field.state-success {
  border-color: var(--color-success);
}

.textarea-field.state-success:focus {
  border-color: var(--color-success);
  box-shadow: 0 0 0 2px var(--color-success);
}

.textarea-field.state-warning {
  border-color: var(--color-warning);
}

.textarea-field.state-warning:focus {
  border-color: var(--color-warning);
  box-shadow: 0 0 0 2px var(--color-warning);
}

.textarea-field.state-danger {
  border-color: var(--color-danger);
}

.textarea-field.state-danger:focus {
  border-color: var(--color-danger);
  box-shadow: 0 0 0 2px var(--color-danger);
}
</style>
